/**
 * Route parsing (U15). client-view is deliberately dependency-light: no
 * router library — a pure path parser drives four screens.
 *
 * Canonical URLs (ARCHITECTURE.md):
 *   {APP_URL}/p/<token>          proposal viewer  (public proposal token)
 *   {APP_URL}/p/invite/<token>   sub invite claim
 *   {APP_URL}/p/c/<token>        change order viewer
 *   {APP_URL}/p/join/<token>     join a company/project by link
 *   {APP_URL}/p/join             join by code
 *   /print?session=<token>       Puppeteer print target (PdfService, U14)
 *
 * The app is served at `/p/*` in production and at `/` by the Vite dev
 * server, so a leading `/p` prefix is stripped when present.
 */

export type Route =
  | { kind: 'proposal'; token: string }
  | { kind: 'invite'; token: string }
  | { kind: 'changeOrder'; token: string }
  | { kind: 'join'; token: string }
  | { kind: 'joinCode' }
  | { kind: 'print'; session: string | null }
  | { kind: 'missing' };

const TOKEN_REGEX = /^[A-Za-z0-9_-]{20,100}$/;

export function parseRoute(pathname: string, search: string): Route {
  let path = pathname.replace(/\/+$/, '');
  if (path === '/p') path = '';
  else if (path.startsWith('/p/')) path = path.slice(2);

  if (path === '/print') {
    return { kind: 'print', session: new URLSearchParams(search).get('session') };
  }

  const invite = /^\/invite\/([^/]+)$/.exec(path);
  if (invite && TOKEN_REGEX.test(invite[1])) {
    return { kind: 'invite', token: invite[1] };
  }

  // Change order viewer: /p/c/<token>. Must match before the bare proposal token.
  const changeOrder = /^\/c\/([^/]+)$/.exec(path);
  if (changeOrder && TOKEN_REGEX.test(changeOrder[1])) {
    return { kind: 'changeOrder', token: changeOrder[1] };
  }

  // Join by link: /p/join/<token>; join by code: /p/join. Must match before
  // the bare proposal token.
  const join = /^\/join\/([^/]+)$/.exec(path);
  if (join && TOKEN_REGEX.test(join[1])) {
    return { kind: 'join', token: join[1] };
  }
  if (path === '/join') {
    return { kind: 'joinCode' };
  }

  const proposal = /^\/([^/]+)$/.exec(path);
  if (proposal && TOKEN_REGEX.test(proposal[1])) {
    return { kind: 'proposal', token: proposal[1] };
  }

  return { kind: 'missing' };
}
